import { MDXEditorMethods } from "@mdxeditor/editor";
import { HidroDXEditor } from "../edit";
import { useEffect, useRef, useState } from "react";
import { useBarragemContext } from "../../context";
import { Box, Divider, Typography } from "@mui/material";
import { RequestError } from "../../services";
import { ErrorModal, ErrorOnDialog, LoadingModal } from "../modals";
import { Controle } from "../../services/api/controle";
import { formatBrazilianPhone } from "../../core";

export const SalaControle: React.FC = () => {
  const { barragem } = useBarragemContext();

  const descricaoRef = useRef<MDXEditorMethods>(null);
  const localizacaoRef = useRef<MDXEditorMethods>(null);
  const instrumentosRef = useRef<MDXEditorMethods>(null);
  const [error, setError] = useState<RequestError>();
  const [salaControle, setSalaControle] = useState<Controle | null>();


  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    if (barragem.salaControle === null) {
      setSalaControle(barragem.salaControle);
      return;
    }

    if ("error" in barragem.salaControle) {
      setError(barragem.salaControle);
      return;
    }

    setTimeout(() => {
      if (barragem.salaControle === null || "error" in barragem.salaControle) {
        return;
      }

      descricaoRef.current?.setMarkdown(barragem.salaControle.descricao ?? "");
      localizacaoRef.current?.setMarkdown(barragem.salaControle.localizacao ?? "");
      instrumentosRef.current?.setMarkdown(barragem.salaControle.instrumentos ?? "");
    }, 100);

    setSalaControle(barragem.salaControle);

  }, [barragem, descricaoRef, localizacaoRef, instrumentosRef]);


  if (salaControle === undefined) return <LoadingModal open={true} />;
  if (salaControle === null) return <ErrorModal error="Sala de Controle" message="A sala de controle ainda não foi definida" />;

  const freqInspecaoText = salaControle?.freqInspecao === 15 ? "Quinzenal" :
    (salaControle?.freqInspecao === 7 ? "Semanal" :
      (salaControle?.freqInspecao === 30 ? "Mensal" :
        (salaControle?.freqInspecao ?? 15).toString() + " dias"));

  return <>
    {/* Error */}
    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    <Box sx={{
      p: "0.5em",
      width: "100%",
      height: "100%",
      display: "flex",
      overflow: "auto",
      flexDirection: "column",
    }}>
      {/* Descrição */}
      <div style={{
        width: "100%",
        textAlign: "justify",
      }}>
        <HidroDXEditor
          readOnly={true}
          editorRef={descricaoRef}
        />
      </div>

      {/* Títulos */}
      <Box sx={{ display: "flex", justifyContent: "center", border: "0.5px solid black" }}>
        <Typography fontSize={18} fontWeight={650}>Sala de Controle e Monitoramento da Barragem</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", border: "0.5px solid black" }}>
        <Typography fontSize={18}>Sala de Controle</Typography>
      </Box>

      {/* Items da sala de controle */}
      <Item title={"Nome"} value={salaControle.nome} />

      <Item title={"Local"} value={
        <div style={{ width: "100%", textAlign: "center" }}>
          <HidroDXEditor
            readOnly={true}
            editorRef={localizacaoRef}
          />
        </div>
      } />

      <Item title={"Supervisórios"} value={
        <Typography component="div">
          {(salaControle.supervisorios ?? "").split(";").map((parte, index) => (
            parte === "" ? <div key={index} /> :
              <div key={index}>
                {parte}
                {index < salaControle.supervisorios.split(";").length - 1 && <br />}
              </div>
          ))}
        </Typography>
      } />
      <Item title={"Telefone"} value={
        <Typography component="div">
          {(salaControle.telefones ?? "").split(";").map((parte, index) => (
            parte === "" ? <></> :
              <div key={index}>
                {formatBrazilianPhone(parte)}
                {index < salaControle.telefones.split(";").length - 1 && <br />}
              </div>
          ))}
        </Typography>
      } />
      <Item title={"Horário de Funcionamento"} value={salaControle.funcionamento + "h"} />


      {/* Título */}
      <Box sx={{ display: "flex", justifyContent: "center", border: "0.5px solid black" }}>
        <Typography fontSize={18}>Monitoramento da Barragem</Typography>
      </Box>

      {/* Items do monitoramento da barragem */}
      <Item title={"Frequência Mínima de Inspeção"} value={freqInspecaoText} />

      <Item title={"Instrumentação no Reservatório"} value={
        <div style={{ width: "100%", textAlign: "center" }}>
          <HidroDXEditor
            readOnly={true}
            editorRef={instrumentosRef}
          />
        </div>} />
    </Box>
  </>;
};


interface ItemProps {
  title: string;
  value: React.ReactNode;
}

const Item: React.FC<ItemProps> = ({ title, value }) => {
  return <Box sx={{
    gap: "1em",
    p: "0.02em",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    border: "0.5px solid black",
  }}>
    <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Typography fontSize={16}>{title}</Typography>
    </div>

    <Divider orientation='vertical' />

    <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
      {typeof value === "string" ? <Typography fontSize={16}>{value}</Typography> : value}
    </div>
  </Box>;
};